<template>
  <div>
    <div class="space-y-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
          <span class="material-icons-round">leaderboard</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">Drivers Leaderboard</h2>
          <p class="text-sm text-gray-500">Top-performing drivers ranked by activity.</p>
        </div>
      </div>
      <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">filter_list</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Filters</span>
          </div>
          <export-button
            :downloadUrl="reportDownloadUrl"
            :working="loading"
          ></export-button>
        </div>

        <div class="flex flex-wrap items-end gap-4">
          <!-- Date range -->
          <div class="w-full sm:w-auto">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Date Range</label>
            <div class="flex items-center h-10 mt-1.5 bg-white border border-gray-300 shadow-sm rounded-xl">
              <span class="pl-3 pr-1 text-base text-gray-500 material-icons-round">event</span>
              <v-date-picker v-model="range" is-range>
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="flex items-center">
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      placeholder="From"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                    <span class="mx-1 text-base text-gray-300 material-icons-round">arrow_forward</span>
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      placeholder="To"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                  </div>
                </template>
              </v-date-picker>
              <button
                v-if="range.start || range.end"
                @click="range = { start: null, end: null }"
                type="button"
                title="Clear dates"
                class="flex items-center justify-center w-8 h-8 mx-1 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
              >
                <span class="text-lg material-icons-round">close</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-4 overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-for="col in ['Rank', 'Driver Name', 'Phone Number', 'Number of Trips']"
                  :key="`col-${col}`"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody class="relative" v-if="leaderboard.length">
              <tr
                v-for="(row, index) in leaderboard"
                :key="row._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
              >
                <!-- Rank -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    v-if="rank(index) <= 3"
                    class="flex items-center justify-center w-8 h-8 rounded-full"
                    :class="rankClass(rank(index))"
                    :title="`Rank ${rank(index)}`"
                  >
                    <i class="text-lg material-icons-round">emoji_events</i>
                  </span>
                  <span
                    v-else
                    class="flex items-center justify-center w-8 h-8 text-xs font-bold text-gray-500 rounded-full bg-gray-100"
                  >{{ rank(index) }}</span>
                </td>
                <!-- Driver -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-3">
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(row.driver))"
                    >{{ initials(personLabel(row.driver)) }}</span>
                    <p class="font-medium text-gray-900">
                      {{ personLabel(row.driver, "Unknown") }}
                    </p>
                  </div>
                </td>
                <!-- Phone -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ row.driver ? row.driver.phoneNumber : "—" }}
                </td>
                <!-- Trips -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-md text-taxiye-600 bg-taxiye-50">
                    {{ row.numOfTrips }} trips
                  </span>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="4" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-400">
                    <span class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100">
                      <i class="text-3xl material-icons-round">emoji_events</i>
                    </span>
                    <span class="text-sm font-medium text-gray-500">No leaderboard data found</span>
                    <span class="mt-1 text-xs text-gray-400">Try adjusting your filters</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-end justify-between">
        <pagination
          class="flex space-x-2"
          :currentPage="currentPage"
          :total="total"
          @goto="goToPage"
          @setPerPage="setPerPage"
          :perPage="perPage"
          :start="start"
          :end="end"
          :count="paginator.count"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationMixin from "@/mixins/Pagination";
import ExportButton from "@/components/ExportButton";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      leaderboard: [],
      loading: true,

      range: {
        start: null,
        end: null,
      },
    };
  },
  components: {
    ExportButton,
    Pagination,
  },
  watch: {
    currentPage() {
      this.getLeaderBoard();
    },
    perPage() {
      this.currentPage === 1 ? this.getLeaderBoard() : (this.currentPage = 1);
    },
    range: {
      handler() {
        this.getLeaderBoard();
      },
      deep: true,
    },
  },
  created() {
    if (this.$route.query.page) {
      this.currentPage = this.$route.query.page;
    }

    this.getLeaderBoard();
  },
  computed: {
    reportDownloadUrl() {
      return (
        "leaderboard/export?" +
        Object.entries({
          ...Object.fromEntries(
            Object.entries({
              ...(this.range || {}),
            }).filter(([key, val]) => key && val)
          ),
        })
          .map(([key, value]) => [key, value].join("="))
          .join("&")
      );
    },
  },
  methods: {
    rank(index) {
      return (Number(this.currentPage) - 1) * Number(this.perPage) + index + 1;
    },
    rankClass(rank) {
      if (rank === 1) return "text-yellow-500 bg-yellow-100";
      if (rank === 2) return "text-gray-500 bg-gray-200";
      return "text-orange-600 bg-orange-100";
    },
    personLabel(person, fallback = "") {
      if (!person) return fallback;
      const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
      if (!name) return fallback;
      return name
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarClass(name) {
      const palette = [
        "bg-taxiye-100 text-taxiye-600",
        "bg-indigo-50 text-indigo-600",
        "bg-blue-50 text-blue-600",
        "bg-green-50 text-green-600",
        "bg-purple-50 text-purple-600",
        "bg-pink-50 text-pink-600",
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) {
        hash = (hash + name.charCodeAt(i)) % palette.length;
      }
      return palette[hash];
    },
    getLeaderBoard() {
      this.loading = true;
      var path = `driver-leaderboard?limit=${this.perPage}&page=${this.currentPage}`;

      if (this.range && this.range.start && this.range.end) {
        path += "&from=" + this.range.start + "&to=" + this.range.end;
      }

      this.$misc
        .get(path)
        .then((response) => {
          this.leaderboard = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
  },
};
</script>
